import { AddressFormFields } from "@/components/addresses/address-form-fields";
import { toAddressInput } from "@/components/addresses/address-input";
import {
  baseAddressFields,
  baseEmptyAddressValues,
  provinceRefinement
} from "@/components/addresses/address-schema";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { handleActionError } from "@/lib/errors";
import { useCreateAddress } from "@/queries/customer/addresses";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Button, Checkbox, Label } from "@medusajs/ui";
import { Form } from "@repo/design-system";
import { memo, useCallback, useId, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { InlineAddressData } from "../validations";

/**
 * Form schema for checkout address dialog.
 * Built from shared base fields + checkout-specific fields.
 */
const checkoutAddressFormSchema = z
  .object({
    ...baseAddressFields,
    address_name: z.string().max(100).optional().or(z.literal("")),
    saveForLater: z.boolean()
  })
  .superRefine(provinceRefinement)
  .refine(
    (data) => {
      if (data.saveForLater) {
        return data.address_name && data.address_name.length > 0;
      }
      return true;
    },
    {
      message: "Address name is required when saving for later",
      path: ["address_name"]
    }
  );

type CheckoutAddressFormData = z.infer<typeof checkoutAddressFormSchema>;

const emptyFormValues = {
  ...baseEmptyAddressValues,
  address_name: "",
  saveForLater: false
} satisfies CheckoutAddressFormData;

export interface CheckoutAddressDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Type of address being added */
  type: "shipping" | "billing";
  /** Whether this is the user's first address (will set both defaults if saving) */
  isFirstAddress?: boolean;
  /**
   * Callback when address is submitted
   * - For saved addresses: returns the saved address ID
   * - For one-time addresses: returns the inline address data
   */
  onSuccess: (result: {
    savedAddressId?: string;
    inlineAddress?: InlineAddressData;
  }) => void;
}

/**
 * Checkout-specific address dialog with "Save for later" option
 *
 * Features:
 * - Full address form with validation (shared field renderer)
 * - "Save for later" checkbox to optionally save to customer account
 * - Shows address name field only when saving
 * - Returns either saved address ID or inline address data
 */
export const CheckoutAddressDialog = memo(function CheckoutAddressDialog({
  open,
  onOpenChange,
  type,
  isFirstAddress = false,
  onSuccess
}: CheckoutAddressDialogProps) {
  const formId = useId();
  const [serverError, setServerError] = useState<string | null>(null);
  const createAddressMutation = useCreateAddress();

  const form = useForm<CheckoutAddressFormData>({
    resolver: zodResolver(checkoutAddressFormSchema),
    defaultValues: emptyFormValues
  });

  const {
    formState: { errors },
    watch,
    reset
  } = form;

  const saveForLater = watch("saveForLater");
  const isSubmitting = createAddressMutation.isPending;

  const handleClose = useCallback(() => {
    setServerError(null);
    reset(emptyFormValues);
    onOpenChange(false);
  }, [onOpenChange, reset]);

  const handleSubmit = form.handleSubmit(async (data) => {
    setServerError(null);

    if (data.saveForLater) {
      // Save to customer account
      try {
        const result = await createAddressMutation.mutateAsync({
          address_name: data.address_name || "",
          ...toAddressInput(data),
          is_default_shipping: isFirstAddress && type === "shipping",
          is_default_billing: isFirstAddress && type === "billing"
        });

        // Get the newly created address ID from the response
        const newAddressId =
          result.customer?.addresses?.[result.customer.addresses.length - 1]
            ?.id;

        handleClose();
        onSuccess({ savedAddressId: newAddressId });
      } catch (error) {
        setServerError(
          handleActionError(error, {
            boundary: "checkout address dialog",
            fallback: "We couldn't save this address. Please try again."
          })
        );
      }
    } else {
      // Use as one-time address (don't save to account)
      const inlineAddress: InlineAddressData = {
        first_name: data.first_name,
        last_name: data.last_name,
        company: data.company || "",
        address_1: data.address_1,
        address_2: data.address_2 || "",
        city: data.city,
        postal_code: data.postal_code,
        province: data.province || "",
        country_code: data.country_code,
        phone: data.phone || ""
      };

      handleClose();
      onSuccess({ inlineAddress });
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
        aria-describedby="checkout-address-dialog-description"
      >
        <DialogHeader>
          <DialogTitle>
            Add {type === "shipping" ? "Shipping" : "Billing"} Address
          </DialogTitle>
          <DialogDescription id="checkout-address-dialog-description">
            Enter your {type} address details. You can optionally save it to
            your account for future use.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            id={formId}
            // Stop here: React bubbles a submit through the portal to the
            // checkout step's own <form>, so "Use This Address" also submitted
            // the whole Delivery step - jumping to Payment before the new
            // address's shipping rates had even loaded (KAN-230).
            onSubmit={(event) => {
              event.stopPropagation();
              void handleSubmit(event);
            }}
            className="space-y-4"
            aria-label={`${type} address form`}
          >
            <AddressFormFields
              form={form}
              showAddressName={saveForLater}
              slotBefore={
                <>
                  {/* Save for later checkbox */}
                  <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <Checkbox
                      id={`${formId}-save-for-later`}
                      checked={saveForLater}
                      onCheckedChange={(checked) =>
                        form.setValue("saveForLater", !!checked)
                      }
                    />
                    <div className="space-y-1">
                      <Label
                        htmlFor={`${formId}-save-for-later`}
                        className="cursor-pointer text-sm font-medium"
                      >
                        Save this address for future orders
                      </Label>
                      <p className="text-xs text-gray-500">
                        Address will be saved to your account
                      </p>
                    </div>
                  </div>
                </>
              }
            />

            {/* Server Error */}
            {serverError && (
              <Alert variant="error" dismissible>
                {serverError}
              </Alert>
            )}

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                {saveForLater ? "Save & Use Address" : "Use This Address"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});
